'use client';

import { c as _c } from "react/compiler-runtime";
import { type CSSProperties, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import type { SharedAnimatedValueProps } from '@/app/lcv/components/sections/spartan/shared/animated-value/types';
import { clamp, easeOutQuint, easeOutQuintSpeed, getDecimalPlaces, getFormatter, getLaneCount, setBlur, splitNumberLanes } from '@/app/lcv/components/sections/spartan/shared/animated-value/utils';
import { cn } from '@/app/utils/cn';
import styles from './styles.module.css';
const DURATION_MS = 900;
const BLUR_STRENGTH = 18;
const MAX_BLUR = 10;
const FADE_IN_PROGRESS = 0.2;
export const SharedAnimatedValue = t0 => {
  const $ = _c(96);
  let className;
  let play;
  let progressRef;
  let rest;
  let style;
  let t1;
  let t2;
  let t3;
  let value;
  let valueWrapperClassName;
  if ($[0] !== t0) {
    ({
      value,
      animateOnEnter: t1,
      play,
      progressRef,
      className,
      style,
      durationMs: t2,
      valueWrapperClassName,
      direction: t3,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = play;
    $[3] = progressRef;
    $[4] = rest;
    $[5] = style;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = value;
    $[10] = valueWrapperClassName;
  } else {
    className = $[1];
    play = $[2];
    progressRef = $[3];
    rest = $[4];
    style = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    value = $[9];
    valueWrapperClassName = $[10];
  }
  const animateOnEnter = t1 === undefined ? true : t1;
  const durationMs = t2 === undefined ? DURATION_MS : t2;
  const direction = t3 === undefined ? "auto" : t3;
  const isDriven = !!progressRef;
  const isCued = !isDriven && play !== undefined;
  const startsFromZero = animateOnEnter || isDriven || isCued;
  const t4 = useId();
  let t5;
  if ($[11] !== t4) {
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = /:/g;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = t4.replace(t6, "");
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const reactId = t5;
  let t6;
  if ($[14] !== value) {
    t6 = getDecimalPlaces(value);
    $[14] = value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const initialDecimals = t6;
  let t7;
  if ($[16] !== initialDecimals) {
    t7 = getFormatter(initialDecimals);
    $[16] = initialDecimals;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const initialFormatter = t7;
  let t8;
  if ($[18] !== initialFormatter || $[19] !== value) {
    t8 = () => getLaneCount(initialFormatter, value);
    $[18] = initialFormatter;
    $[19] = value;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const [laneCount, setLaneCount] = useState(t8);
  let t9;
  if ($[21] !== initialFormatter || $[22] !== value) {
    t9 = () => splitNumberLanes(initialFormatter, value).mark;
    $[21] = initialFormatter;
    $[22] = value;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const [groupMark, setGroupMark] = useState(t9);
  let t10;
  if ($[24] !== startsFromZero || $[25] !== value) {
    t10 = () => startsFromZero ? 0 : value;
    $[24] = startsFromZero;
    $[25] = value;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const [seedValue, setSeedValue] = useState(t10);
  let t11;
  if ($[27] !== initialFormatter || $[28] !== value) {
    t11 = () => initialFormatter.format(value);
    $[27] = initialFormatter;
    $[28] = value;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const [accessibleLabel, setAccessibleLabel] = useState(t11);
  let t12;
  if ($[30] !== animateOnEnter || $[31] !== isCued || $[32] !== play) {
    t12 = () => isCued ? Boolean(play) : !animateOnEnter;
    $[30] = animateOnEnter;
    $[31] = isCued;
    $[32] = play;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const [hasStarted, setHasStarted] = useState(t12);
  let t13;
  bb0: {
    if (direction !== "auto") {
      t13 = direction;
      break bb0;
    }
    t13 = Math.abs(value) < 100 ? "vertical" : "horizontal";
  }
  const activeDirection = t13;
  const rootRef = useRef(null);
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const digitRefs = useRef(t14);
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [];
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const separatorRefs = useRef(t15);
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = [];
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const blurRefs = useRef(t16);
  const laneCountRef = useRef(laneCount);
  const currentValueRef = useRef(startsFromZero ? 0 : value);
  const targetValueRef = useRef(value);
  const settledDecimalsRef = useRef(initialDecimals);
  const directionRef = useRef(activeDirection);
  const rafRef = useRef(null);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", false) ?? false;
  let t17;
  let t18;
  if ($[37] !== activeDirection || $[38] !== value) {
    t17 = () => {
      targetValueRef.current = value;
      directionRef.current = activeDirection;
    };
    t18 = [value, activeDirection];
    $[37] = activeDirection;
    $[38] = value;
    $[39] = t17;
    $[40] = t18;
  } else {
    t17 = $[39];
    t18 = $[40];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[41] !== animateOnEnter || $[42] !== isCued || $[43] !== isDriven || $[44] !== play || $[45] !== reducedMotion) {
    t19 = () => {
      if (isDriven) {
        return;
      }
      if (isCued) {
        if (play) {
          setHasStarted(true);
        }
        return;
      }
      if (!animateOnEnter || reducedMotion || !rootRef.current) {
        setHasStarted(true);
        return;
      }
      const node = rootRef.current;
      const revealGroup = node.closest("[data-spartan-reveal]");
      let fallbackObserver = null;
      let revealObserver = null;
      if (revealGroup) {
        const syncRevealState = () => {
          if (!revealGroup.classList.contains("is-inview")) {
            return;
          }
          setHasStarted(true);
          revealObserver?.disconnect();
        };
        syncRevealState();
        if (!revealGroup.classList.contains("is-inview")) {
          revealObserver = new MutationObserver(syncRevealState);
          revealObserver.observe(revealGroup, {
            attributeFilter: ["class"],
            attributes: true
          });
        }
      } else {
        if (typeof IntersectionObserver === "undefined") {
          setHasStarted(true);
        } else {
          fallbackObserver = new IntersectionObserver(t21 => {
            const [entry] = t21;
            if (!entry?.isIntersecting) {
              return;
            }
            setHasStarted(true);
            fallbackObserver?.disconnect();
          }, {
            rootMargin: "0px 0px -20% 0px"
          });
          fallbackObserver.observe(node);
        }
      }
      return () => {
        fallbackObserver?.disconnect();
        revealObserver?.disconnect();
      };
    };
    t20 = [animateOnEnter, isCued, isDriven, play, reducedMotion];
    $[41] = animateOnEnter;
    $[42] = isCued;
    $[43] = isDriven;
    $[44] = play;
    $[45] = reducedMotion;
    $[46] = t19;
    $[47] = t20;
  } else {
    t19 = $[46];
    t20 = $[47];
  }
  useEffect(t19, t20);
  let t21;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = active => {
      rootRef.current?.classList.toggle(styles.animating, active);
    };
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const setAnimating = t21;
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = (nextValue, speed, decimals, t23, t24) => {
      const progress = t23 === undefined ? 1 : t23;
      const fadeIn = t24 === undefined ? false : t24;
      const formatter = getFormatter(decimals);
      const opacity = fadeIn ? clamp(progress / FADE_IN_PROGRESS, 0, 1) : 1;
      const {
        body
      } = splitNumberLanes(formatter, nextValue);
      const lanes = laneCountRef.current;
      const offset = lanes - body.length;
      const dir = directionRef.current;
      const absTarget = Math.abs(targetValueRef.current);
      const rangeScale = dir === "vertical" && absTarget > 0 && absTarget < 100 ? 100 / absTarget : 1;
      const effectiveSpeed = speed * rangeScale;
      for (let lane = 0; lane < lanes; lane = lane + 1, lane) {
        const bodyIndex = lane - offset;
        const visible = bodyIndex >= 0;
        const digitNode = digitRefs.current[lane];
        const separatorNode = separatorRefs.current[lane];
        const blurNode = blurRefs.current[lane];
        if (digitNode) {
          digitNode.textContent = visible ? body[bodyIndex] : "";
          digitNode.style.opacity = visible ? String(opacity) : "0";
          digitNode.style.transform = "translate3d(0, 0, 0)";
        }
        if (separatorNode) {
          separatorNode.style.opacity = visible ? "1" : "0";
        }
        const placeFalloff = Math.pow(1000, lanes - 1 - lane);
        const blur = clamp(effectiveSpeed / placeFalloff * (BLUR_STRENGTH / 1000), 0, MAX_BLUR);
        setBlur(blurNode, blur, dir);
      }
    };
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const paint = t22;
  let t23;
  let t24;
  if ($[50] !== durationMs || $[51] !== hasStarted || $[52] !== isDriven || $[53] !== reducedMotion || $[54] !== value) {
    t23 = () => {
      const endValue = value;
      const startValue = currentValueRef.current;
      const endDecimals = getDecimalPlaces(endValue);
      const decimals_0 = Math.max(settledDecimalsRef.current, endDecimals);
      const formatter_0 = getFormatter(decimals_0);
      const nextLaneCount = getLaneCount(formatter_0, startValue, endValue);
      const nextMark = splitNumberLanes(formatter_0, endValue).mark;
      const lanesChanged = nextLaneCount !== laneCountRef.current;
      laneCountRef.current = nextLaneCount;
      setLaneCount(nextLaneCount);
      setGroupMark(nextMark);
      if (isDriven) {
        return;
      }
      const cancelRaf = () => {
        if (rafRef.current != null) {
          window.cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
      const settle = () => {
        currentValueRef.current = endValue;
        settledDecimalsRef.current = endDecimals;
        setSeedValue(endValue);
        setAccessibleLabel(getFormatter(endDecimals).format(endValue));
        setAnimating(false);
        paint(endValue, 0, endDecimals, 1);
      };
      const run = callback => {
        if (lanesChanged) {
          rafRef.current = window.requestAnimationFrame(callback);
          return;
        }
        callback();
      };
      cancelRaf();
      if (!hasStarted) {
        setAnimating(false);
        return () => cancelRaf();
      }
      if (reducedMotion || startValue === endValue) {
        run(settle);
        return () => cancelRaf();
      }
      const distance = Math.abs(endValue - startValue);
      const startedAt = performance.now();
      const fadeIn_0 = startValue === 0;
      const frame = now => {
        const progress_0 = clamp((now - startedAt) / durationMs, 0, 1);
        const eased = easeOutQuint(progress_0);
        const nextValue_0 = startValue + (endValue - startValue) * eased;
        const speed_0 = easeOutQuintSpeed(progress_0) * distance * (1000 / durationMs);
        currentValueRef.current = nextValue_0;
        paint(nextValue_0, speed_0, decimals_0, progress_0, fadeIn_0);
        if (progress_0 < 1) {
          rafRef.current = window.requestAnimationFrame(frame);
          return;
        }
        settle();
        rafRef.current = null;
      };
      run(() => {
        setAnimating(true);
        paint(startValue, easeOutQuintSpeed(0) * distance * (1000 / durationMs), decimals_0, 0, fadeIn_0);
        rafRef.current = window.requestAnimationFrame(frame);
      });
      return () => {
        cancelRaf();
        setAnimating(false);
      };
    };
    t24 = [hasStarted, isDriven, reducedMotion, value, durationMs];
    $[50] = durationMs;
    $[51] = hasStarted;
    $[52] = isDriven;
    $[53] = reducedMotion;
    $[54] = value;
    $[55] = t23;
    $[56] = t24;
  } else {
    t23 = $[55];
    t24 = $[56];
  }
  useEffect(t23, t24);
  let t25;
  let t26;
  if ($[57] !== progressRef || $[58] !== reducedMotion || $[59] !== value) {
    t25 = () => {
      const playhead = progressRef;
      if (!playhead) {
        return;
      }
      const decimals_1 = getDecimalPlaces(value);
      let frame_0 = 0;
      let lastProgress = -1;
      let lastValue = 0;
      let lastAt = performance.now();
      const tick = now_0 => {
        frame_0 = window.requestAnimationFrame(tick);
        const progress_1 = clamp(playhead.value, 0, 1);
        if (progress_1 === lastProgress) {
          lastAt = now_0;
          return;
        }
        const nextValue_1 = reducedMotion ? progress_1 > 0 ? value : 0 : value * progress_1;
        const speed_1 = Math.abs(nextValue_1 - lastValue) * 1000 / Math.max(now_0 - lastAt, 1);
        lastProgress = progress_1;
        lastValue = nextValue_1;
        lastAt = now_0;
        currentValueRef.current = nextValue_1;
        setAnimating(progress_1 > 0 && progress_1 < 1);
        paint(nextValue_1, speed_1, decimals_1, progress_1, true);
      };
      frame_0 = window.requestAnimationFrame(tick);
      return () => {
        window.cancelAnimationFrame(frame_0);
        setAnimating(false);
      };
    };
    t26 = [progressRef, reducedMotion, value];
    $[57] = progressRef;
    $[58] = reducedMotion;
    $[59] = value;
    $[60] = t25;
    $[61] = t26;
  } else {
    t25 = $[60];
    t26 = $[61];
  }
  useEffect(t25, t26);
  let t27;
  if ($[62] !== initialFormatter || $[63] !== value) {
    t27 = splitNumberLanes(initialFormatter, value);
    $[62] = initialFormatter;
    $[63] = value;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const targetLanes = t27;
  const isWaitingToStart = !isDriven && startsFromZero && !hasStarted;
  let t28;
  if ($[65] !== seedValue) {
    t28 = getDecimalPlaces(seedValue);
    $[65] = seedValue;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  const seedDecimals = t28;
  let t29;
  if ($[67] !== seedDecimals || $[68] !== seedValue) {
    t29 = splitNumberLanes(getFormatter(seedDecimals), seedValue);
    $[67] = seedDecimals;
    $[68] = seedValue;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  const seedLanes = t29;
  let t30;
  if ($[70] !== className) {
    t30 = cn(styles.number, className);
    $[70] = className;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  const t31 = style as CSSProperties;
  let t32;
  if ($[72] !== laneCount || $[73] !== reactId) {
    t32 = <svg aria-hidden={true} className={styles.filterSvg}><defs>{Array.from({
          length: laneCount
        }, (_, lane_0) => <filter key={`${reactId}-filter-${lane_0}`} id={`${reactId}-${lane_0}`} x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur ref={node_0 => {
            blurRefs.current[lane_0] = node_0;
          }} in="SourceGraphic" stdDeviation="0,0" /></filter>)}</defs></svg>;
    $[72] = laneCount;
    $[73] = reactId;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== accessibleLabel) {
    t33 = <span className="sr-only">{accessibleLabel}</span>;
    $[75] = accessibleLabel;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  let t34;
  if ($[77] !== valueWrapperClassName) {
    t34 = cn("inline-flex items-baseline whitespace-pre", valueWrapperClassName);
    $[77] = valueWrapperClassName;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== groupMark || $[80] !== isWaitingToStart || $[81] !== laneCount || $[82] !== reactId || $[83] !== seedLanes || $[84] !== targetLanes) {
    t35 = Array.from({
      length: laneCount
    }, (__0, lane_1) => {
      const offset_0 = laneCount - seedLanes.body.length;
      const bodyIndex_0 = lane_1 - offset_0;
      const seedText = bodyIndex_0 >= 0 ? seedLanes.body[bodyIndex_0] ?? "" : "";
      const targetOffset = laneCount - targetLanes.body.length;
      const targetBodyIndex = lane_1 - targetOffset;
      const targetText = targetBodyIndex >= 0 ? targetLanes.body[targetBodyIndex] ?? "" : "";
      return <span key={`${reactId}-lane-${lane_1}`} className="inline-flex items-baseline">{lane_1 > 0 ? <span ref={node_1 => {
          separatorRefs.current[lane_1] = node_1;
        }} className={styles.separator} style={{
          opacity: bodyIndex_0 >= 0 ? 1 : 0
        }} aria-hidden={true}>{groupMark}</span> : null}<span className={styles.lane}><span className={styles.sizer}>{targetText}</span><span ref={node_2 => {
            digitRefs.current[lane_1] = node_2;
          }} className={cn(styles.glyph, lane_1 > 0 && styles.glyphGrouped)} style={{
            filter: `url(#${reactId}-${lane_1})`,
            opacity: bodyIndex_0 >= 0 && !isWaitingToStart ? 1 : 0
          }}>{seedText}</span></span></span>;
    });
    $[79] = groupMark;
    $[80] = isWaitingToStart;
    $[81] = laneCount;
    $[82] = reactId;
    $[83] = seedLanes;
    $[84] = targetLanes;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== t34 || $[87] !== t35) {
    t36 = <span className={t34} aria-hidden={true}>{t35}</span>;
    $[86] = t34;
    $[87] = t35;
    $[88] = t36;
  } else {
    t36 = $[88];
  }
  let t37;
  if ($[89] !== rest || $[90] !== t30 || $[91] !== t31 || $[92] !== t32 || $[93] !== t33 || $[94] !== t36) {
    t37 = <span ref={rootRef} className={t30} style={t31} {...rest}>{t32}{t33}{t36}</span>;
    $[89] = rest;
    $[90] = t30;
    $[91] = t31;
    $[92] = t32;
    $[93] = t33;
    $[94] = t36;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  return t37;
};